/**
 * FreshCards — Root App Component
 *
 * Composes: WorkspaceProvider → PeekManagerProvider → MainWorkspace
 * MainWorkspace renders: WorkspaceHeader + WorkspaceSurface + peek panels
 */
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { WorkspaceProvider } from './core/workspace/WorkspaceContext';
import { PeekManagerProvider, usePeek } from './ui/components/common/PeekManager';
import { FocusModeProvider } from './ui/components/cockpit/FocusMode/FocusModeProvider';
import WorkspaceSurface from './ui/components/surfaces/WorkspaceSurface';
import { WorkspaceHeader } from './ui/components/common/WorkspaceHeader';
import { QuickNotePanel } from './ui/components/common/QuickNotePanel';
import { AIChatPanel } from './ui/components/common/AIChatPanel';
import { exposePeekHooks } from './ui/components/common/PeekHooks';
import { ThemeProvider } from './ui/themes';
import { AuthProvider, useAuth } from './ui/components/auth/AuthContext';
import LoginScreen from './ui/components/auth/LoginScreen';
import LaneChat from './ui/components/surfaces/LaneChat';
import { Box, Center, Stack, Loader, Text } from '@mantine/core';
import '@mantine/core/styles.css';
// 2026-09-18 (PROMPT F 21) — Minimal Cmd+K command palette. Closes Gap 2.
import { CommandPalette } from './ui/components/common/CommandPalette';
// F 490 — the reminder feature. The provider owns the single ReminderStore
// and the single notification poll, so the header's bell and the toast can
// never disagree about what is unread. The toast itself is mounted at the
// app's root, outside the scroll container, because a toast that scrolls
// with the page is a toast that gets missed.
import { ReminderProvider, useReminderContext } from './ui/components/common/ReminderProvider';
import { NotificationToast } from './ui/components/common/NotificationToast';
// F 452 — the offline banner. Mounted at the app's root, beside the toast,
// because it is a standing condition and not a message: it must not scroll
// away with the page.
import { OfflineBanner } from './ui/components/common/OfflineBanner';
// EmbeddingAPIv2 demo (Phase 4) — exercises the host contract so the bundle
// includes mountRuntime / EmbeddingAPIv2 / HostRenderer / FreshCardsHostAdapter.
// Toggle via ?embed-demo in the URL. Hidden by default so the SPA UX is unchanged.
import { FreshCardsEmbedDemo } from './components/embedding/FreshCardsEmbedDemo';

// F 324 — the SETTINGS SURFACE, reachable.
//
// The settings surface existed but nothing imported it: it was not in the
// surface registry, not in the router, and not in the workspace, so it never
// reached the bundle. A settings page nobody can open cannot show the
// telemetry-retention setting, which is the whole point of the graph-driven
// page.
//
// It is reachable the same way the embed demo is — a URL flag — so the
// default path of the app is untouched:
//     ?settings
const Settings = lazy(() => import('./ui/components/surfaces/Settings').then((m) => ({ default: m.Settings })));

/** Shown while workspace initializes */
const WorkspaceLoading: React.FC = () => (
  <Center h="100vh" w="100vw">
    <Stack align="center" gap="md">
      <Loader size="lg" />
      <Text c="dimmed" size="sm">Loading workspace...</Text>
    </Stack>
  </Center>
);

/** Composes header + surface + conditional peek panels */
const MainWorkspace: React.FC = () => {
  const { isPeekOpen } = usePeek();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const showEmbedDemo = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('embed-demo');
  // F 324 — `?settings` opens the graph-driven settings surface.
  const showSettings = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('settings');
  // F 381 — `?lane-chat` opens the Lane Chat surface, the window onto the
  // Lane Bridge. It is its OWN surface and not a mode of the Agent Chat:
  // the Agent Chat runs a Groq tool loop in the browser, and the Lane Chat
  // only reads and writes `turn` nodes for the bridge to answer.
  const showLaneChat = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('lane-chat');

  // 2026-09-18 (PROMPT F 21) — Global keyboard listener for Cmd+K / Ctrl+K.
  // Toggle palette; Esc closes when open. Listener is on window so it works
  // regardless of focused element.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isToggle = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      const isEsc = e.key === 'Escape';
      if (isToggle) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
        return;
      }
      if (isEsc && paletteOpen) {
        setPaletteOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [paletteOpen]);

  return (
    <Box style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <OfflineBanner />
      <WorkspaceHeader />
      <Box style={{ flex: '1 1 auto', overflow: 'auto' }}>
        {showSettings ? (
          <Suspense fallback={<WorkspaceLoading />}>
            <Settings id="settings" phase={1} title="Settings" />
          </Suspense>
        ) : showLaneChat ? (
          <Suspense fallback={<WorkspaceLoading />}>
            <LaneChat id="lane-chat" />
          </Suspense>
        ) : (
          <>
            <WorkspaceSurface />
            {showEmbedDemo && <FreshCardsEmbedDemo />}
          </>
        )}
      </Box>
      {isPeekOpen('quick-note') && <QuickNotePanel />}
      {isPeekOpen('ai-chat') && <AIChatPanel />}
      <CommandPalette opened={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </Box>
  );
};

/**
 * F 374 — THE LOGIN GATE.
 *
 * The app is behind the session. The session holds the PASSPHRASE, which is
 * what the credential store's AES-GCM key is derived from — so the surfaces
 * that read credentials (the chat, Settings) need the session to exist before
 * they can read anything.
 *
 * The dev bypass lives in the AuthProvider, not here, and it fires on the
 * ENVIRONMENT (a `dev` build, or a `localhost` hostname). On production it
 * never fires, so this gate is what a real visitor gets.
 */
const AuthGate: React.FC = () => {
  const auth = useAuth();

  if (auth.status === 'checking') {
    return (
      <Center h="100vh" w="100vw">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed" size="sm">Checking your session…</Text>
        </Stack>
      </Center>
    );
  }

  if (auth.status === 'locked') {
    return (
      <LoginScreen
        onSubmit={auth.login}
        error={auth.error}
        busy={auth.busy}
      />
    );
  }

  return <MainWorkspace />;
};

/**
 * F 490 — the reminder provider and the toast.
 *
 * The provider wraps the whole workspace, because the header's bell and the
 * date surfaces' clocks are in different subtrees and must share ONE store
 * and ONE poll.
 *
 * The toast is mounted HERE, at the app's root, and NOT inside the scroll
 * container: a toast that scrolls away with the page is a toast that gets
 * missed, and a reminder the operator never saw is a reminder that did not
 * happen.
 */
const ReminderShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ReminderProvider>
    {children}
    <NotificationToastHost />
  </ReminderProvider>
);

/** The toast, wired to the provider's pending notification. */
const NotificationToastHost: React.FC = () => {
  const { pendingToast, dismissToast, markRead } = useReminderContext();
  return (
    <NotificationToast
      notification={pendingToast}
      onDismiss={dismissToast}
      onMarkRead={(n) => void markRead(n.id)}
    />
  );
};

/** Full provider tree — exposes hooks to window on mount */
const FullApp: React.FC = () => {
  useEffect(() => { exposePeekHooks(); }, []);
  return (
    <ThemeProvider>
      <AuthProvider>
        <WorkspaceProvider>
          <PeekManagerProvider initialPeekId={null} initialWidth={320}>
            <FocusModeProvider>
              <ReminderShell>
                <AuthGate />
              </ReminderShell>
            </FocusModeProvider>
          </PeekManagerProvider>
        </WorkspaceProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

const App: React.FC = () => <FullApp />;
export default App;